import React from "react";
import { Circle, PenLine, Plus, SquareCheckBig, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ButtonComponent } from "../components/ui/buttonComponent";
import { Input } from "../components/ui/input";
import { TextArea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import SortableQuestion from "../components/SortableQuestion";
import type { SurveyQuestion } from "../types/defaultTypes";
import type { ValidationError } from "../utils/validateSurvey";
import type { SurveyInfo } from "../types/defaultTypes";
import {
  addQuestion,
  questionTypeLabel,
  updateQuestion,
  addOption,
  removeOption,
  updateOption,
  updateCorrect,
  reorderOptions,
} from "../utils/SurveyManageUtils";

type SurveyFormProps = {
  sensors: any;
  surveyInfo: SurveyInfo;
  setSurveyInfo: React.Dispatch<React.SetStateAction<SurveyInfo>>;
  questions: SurveyQuestion[];
  setQuestions: React.Dispatch<React.SetStateAction<SurveyQuestion[]>>;
  formErrors: ValidationError | null;
};

const SurveyForm: React.FC<SurveyFormProps> = ({
  sensors,
  surveyInfo,
  setSurveyInfo,
  questions,
  setQuestions,
  formErrors,
}) => {
  const handleAddQuestion = (type: SurveyQuestion["type"]) => setQuestions((prev) => addQuestion(prev, type));

  const handleUpdateQuestion = (id: string, updates: Partial<SurveyQuestion>) =>
    setQuestions((prev) => updateQuestion(prev, id, updates));

  const handleAddOption = (qid: string) => setQuestions((prev) => addOption(prev, qid));

  const handleRemoveOption = (qid: string, oid: string) => setQuestions((prev) => removeOption(prev, qid, oid));

  const handleUpdateOption = (qid: string, oid: string, ans: string) =>
    setQuestions((prev) => updateOption(prev, qid, oid, ans));

  const handleUpdateCorrect = (qid: string, type: SurveyQuestion["type"], oid: string) =>
    setQuestions((prev) => updateCorrect(prev, qid, type, oid));

  const handleReorderOptions = (qid: string, active: string, over: string) =>
    setQuestions((prev) => reorderOptions(prev, qid, active, over));

  const handleRemoveQuestion = (qid: string) =>
    setQuestions((prev) => prev.filter((q) => q.id !== qid).map((q, idx) => ({ ...q, order: idx + 1 })));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setSurveyInfo((prev) => ({ ...prev, [id]: value }));
  };

  return (
    <>
      {/* Survey Configuration */}
      <div className="shadow flex flex-col gap-5 p-6 rounded-2xl bg-white">
        <h1 className="text-lg font-semibold text-gray-900">Survey Configuration</h1>

        <div className="flex gap-4 flex-wrap">
          <div className="w-[500px] flex flex-col gap-1">
            <p className="text-gray-500 text-sm font-medium">Survey Title</p>
            <Input
              id="title"
              className="!bg-input-bg !rounded-xl"
              value={surveyInfo.title}
              onChange={handleInputChange}
              error={formErrors?.survey?.title}
            />
          </div>
          <div className="w-[500px] flex flex-col gap-1">
            <p className="text-gray-500 text-sm font-medium">Send Delay in Days</p>
            <Input
              id="surveyTrigger"
              className="!bg-input-bg !rounded-xl"
              value={surveyInfo.surveyTrigger}
              onChange={handleInputChange}
              error={formErrors?.survey?.surveyTrigger}
            />
          </div>
        </div>

        <div className="flex gap-4 flex-wrap">
          <div className="w-[500px] flex flex-col gap-1">
            <p className="text-gray-500 text-sm font-medium">Description</p>
            <TextArea
              id="description"
              className="!bg-input-bg !rounded-xl !min-h-[100px]"
              value={surveyInfo.description}
              onChange={handleInputChange}
              error={formErrors?.survey?.description}
            />
          </div>

          <div className="flex flex-col">
            <div className="w-[500px] flex gap-2 items-center py-4">
              <p className="text-gray-500 text-sm font-medium">Auto-send to users</p>
              <Switch
                checked={surveyInfo.autoSend}
                onClick={() => setSurveyInfo((prev) => ({ ...prev, autoSend: !prev.autoSend }))}
              />
            </div>
            <div className="w-[500px] flex gap-2 items-center py-4">
              <p className="text-gray-500 text-sm font-medium">Include Out Of Range Users</p>
              <Switch
                checked={surveyInfo.includeUsers}
                onClick={() => setSurveyInfo((prev) => ({ ...prev, includeUsers: !prev.includeUsers }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="flex justify-between items-center mt-6">
        <div className="flex flex-col gap-1">
          <h1 className="font-semibold text-xl">Survey Questions</h1>
          {formErrors?.survey?.questions && (
            <p className="text-red-500 font-normal text-base">{formErrors?.survey?.questions}</p>
          )}
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <ButtonComponent type="button" className="flex items-center gap-1">
              <Plus className="w-5 h-5" /> Add Question
            </ButtonComponent>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            sideOffset={15}
            align="end"
            className="min-w-[300px] rounded-xl p-1 !z-[10000000] bg-white shadow-lg"
          >
            <div className="p-4 flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <p className="font-semibold text-sm">Select Question Type</p>
                <DropdownMenu.Item className="h-6 w-6 bg-gray-200 flex justify-center items-center rounded-full">
                  <X className="h-4 w-4 text-gray-500" />
                </DropdownMenu.Item>
              </div>

              <div className="flex flex-col gap-1">
                <DropdownMenu.Item onClick={() => handleAddQuestion("open-ended")}>
                  <div className="p-3 py-2 flex gap-3 items-center cursor-pointer">
                    <PenLine className="w-6 h-6 text-xuron-orange" />
                    <div className="flex flex-col gap-0.5">
                      <p className="text-gray-500 text-sm">Open Ended Question</p>
                      <p className="text-gray-500 text-xs">Text input for detailed responses</p>
                    </div>
                  </div>
                </DropdownMenu.Item>

                <DropdownMenu.Item onClick={() => handleAddQuestion("single-choice")}>
                  <div className="p-3 py-2 flex gap-3 items-center cursor-pointer">
                    <Circle className="w-6 h-6 text-xuron-orange" />
                    <div className="flex flex-col gap-0.5">
                      <p className="text-gray-500 text-sm">Single Selection Multiple Choice</p>
                      <p className="text-gray-500 text-xs">Radio buttons for one answer</p>
                    </div>
                  </div>
                </DropdownMenu.Item>

                <DropdownMenu.Item onClick={() => handleAddQuestion("multi-choice")}>
                  <div className="p-3 py-2 flex gap-3 items-center cursor-pointer">
                    <SquareCheckBig className="w-6 h-6 text-xuron-orange" />
                    <div className="flex flex-col gap-0.5">
                      <p className="text-gray-500 text-sm">Multi Selection Multiple Choice</p>
                      <p className="text-gray-500 text-xs">Checkboxes for multiple answers</p>
                    </div>
                  </div>
                </DropdownMenu.Item>
              </div>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>

      {/* Sortable questions */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;
          setQuestions((prev) =>
            arrayMove(
              prev,
              prev.findIndex((q) => q.id === active.id),
              prev.findIndex((q) => q.id === over.id)
            ).map((q, i) => ({ ...q, order: i + 1 }))
          );
        }}
      >
        <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-6 mt-4">
            {questions.map((question, index) => (
              <SortableQuestion
                key={question.id}
                question={question}
                index={index}
                updateQuestion={handleUpdateQuestion}
                reorderOptions={handleReorderOptions}
                updateCorrect={handleUpdateCorrect}
                updateOption={handleUpdateOption}
                removeOption={handleRemoveOption}
                addOption={handleAddOption}
                sensors={sensors}
                questionTypeLabel={questionTypeLabel}
                removeQuestion={handleRemoveQuestion}
                errors={formErrors?.questions.find((qe) => qe.id === question.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
};

export default SurveyForm;
